import { Box, Link, Paper, Typography } from '@mui/material'
import { Flags } from '../../../../../components/common/flag/flags'
import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import MapStaticComponent from '../../../../../components/common/googleMaps/GoogleMapsStaticComponent'
import './checks.scss'
import '../../../components/pdf.scss'
import TimelineDotLine from '../components/TimelineDotLine'

const CompanyDetails = ({ entity, currentCountry, kyb }) => {
  const { t } = useTranslation(['common'])
  const [coordinates, setCoordinates] = useState(null)

  const finalAddress = useMemo(() => {
    const addresses = entity === 'PERSON' ? kyb?.addresses : kyb?.companyDetails?.addresses

    if (Array.isArray(addresses) && addresses.length) {
      const aux = addresses.find((addr) => addr.type === 'PRINCIPAL') || addresses[0]

      if (typeof aux === 'object') {
        if (currentCountry.toUpperCase() === 'CR') {
          return `${aux.district || '-'}, ${aux.city || '-'}, ${aux.state || '-'}, ${aux.postcode || '-'}, ${
            aux.country || '-'
          }`
        }

        return `${aux.street || '-'}, ${aux.number || '-'}, ${aux.city || '-'}, ${aux.state || '-'}, ${
          aux.postcode || '-'
        }, ${aux.country || '-'}`
      }
      return aux
    }

    return ''
  }, [kyb?.addresses, kyb?.companyDetails?.addresses, entity, currentCountry])

  const finalPhones = useMemo(() => {
    if (entity === 'PERSON') {
      if (Array.isArray(kyb?.phoneNumberList) && kyb.phoneNumberList.length > 0) {
        return kyb.phoneNumberList
          .map((phone) => `${phone.AreaCode || phone.ddd || ''} ${phone.Number || phone.numero || ''}`)
          .map((t) => t.trim())
          .join('; ')
      }
      return kyb?.phoneNumber || ''
    }

    if (Array.isArray(kyb?.companyDetails?.telefones) && kyb?.companyDetails?.telefones?.length > 0) {
      return kyb?.companyDetails?.telefones
        .filter((phone) => !!phone && (phone?.ddd || phone?.numero))
        .map((phone) => `${phone.ddd || ''} ${phone.numero || ''}`)
        .map((text) => text.trim())
        .join('; ')
    }

    if (Array.isArray(kyb?.companyDetails?.phones) && kyb?.companyDetails?.phones?.length > 0) {
      return kyb?.companyDetails?.phones.filter((phone) => !!phone).join('; ')
    }

    return ''
  }, [entity, kyb?.phoneNumberList, kyb?.phoneNumber, kyb?.companyDetails?.telefones, kyb?.companyDetails?.phones])

  const finalEmails = useMemo(() => {
    if (entity === 'PERSON') {
      if (Array.isArray(kyb?.emailList) && kyb.emailList.length > 0) {
        return kyb.emailList
          .map((e) => e.EmailAddress || e.email || e)
          .filter(Boolean)
          .join('; ')
      }
      return kyb?.email || ''
    }

    if (Array.isArray(kyb?.companyDetails?.emails) && kyb.companyDetails.emails.length > 0) {
      return kyb.companyDetails.emails.filter(Boolean).join('; ')
    }

    return ''
  }, [entity, kyb?.emailList, kyb?.email, kyb?.companyDetails?.emails])

  return (
    <>
      <Paper elevation={0} variant="colorPrimary">
        Dados da {entity === 'BUSINESS' ? 'empresa' : 'pessoa'} consultada:
      </Paper>

      <Paper
        variant="rootOutlined"
        elevation={0}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '.75rem' }}
      >
        <div className="c-detail-row">
          <Typography variant="subtitle1">
            {currentCountry === 'BR' && entity === 'BUSINESS'
              ? 'CNPJ'
              : entity === 'PERSON'
              ? 'CPF'
              : t('common:documentNumber')}
            :
          </Typography>
          <Typography variant="subtitle3">
            {currentCountry === 'BR' && entity === 'BUSINESS'
              ? kyb?.companyDetails?.cnpj ?? kyb?.companyDetails?.documentNumber
              : entity === 'PERSON'
              ? kyb?.documentNumber
              : '---'}
          </Typography>
        </div>
        <div className="c-detail-row">
          <Typography variant="subtitle1">{t('common:name')}:</Typography>
          <Typography variant="subtitle3">
            {kyb?.companyDetails?.name ? kyb?.companyDetails?.name : kyb?.name ? kyb?.name : '---'}
          </Typography>
        </div>
        {entity === 'PERSON' && (
          <>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:nationality')}:</Typography>
              <Typography variant="subtitle3">{kyb?.nationality ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:gender')}:</Typography>
              <Typography variant="subtitle3">{kyb?.gender ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:motherName')}:</Typography>
              <Typography variant="subtitle3">{kyb?.motherName ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:rendaPresumida')}:</Typography>
              <Typography variant="subtitle3">{kyb?.rendaPresumida ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:situacaoImpostoRenda')}:</Typography>
              <Typography variant="subtitle3">
                {kyb?.situacaoImpostoRenda ? kyb?.situacaoImpostoRenda : '---'}
              </Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:cpfRegistrationDate')}:</Typography>
              <Typography variant="subtitle3">{kyb?.cpfRegistrationDate ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:dateOfBirth')}:</Typography>
              <Typography variant="subtitle3">{kyb?.dateOfBirth ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:address')}:</Typography>
              <Typography variant="subtitle3">{finalAddress || '---'}</Typography>
            </div>
          </>
        )}
        {entity === 'BUSINESS' && (
          <>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:fantasyName')}:</Typography>
              <Typography variant="subtitle3">{kyb?.companyDetails?.fantasia ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:dateOfRegistration')}:</Typography>
              <Typography variant="subtitle3">{kyb?.companyDetails?.dateOfRegistration ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:country')}:</Typography>
              <Typography variant="subtitle3">
                {kyb?.companyDetails?.country ? Flags(kyb?.companyDetails?.country) : '---'}
              </Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:situation')}:</Typography>
              <Typography variant="subtitle3">
                {kyb?.companyDetails?.situacao?.nome || kyb?.companyDetails?.situation || '---'}
              </Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:legalNature')}:</Typography>
              <Typography variant="subtitle3">
                {kyb?.companyDetails?.legalOrganization || kyb?.companyDetails?.naturezaJuridica?.descricao || '---'}
              </Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:capitalSocial')}:</Typography>
              <Typography variant="subtitle3">{kyb?.companyDetails?.capitalSocial ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:simplesNacional')}:</Typography>
              <Typography variant="subtitle3">{kyb?.companyDetails?.simplesNacional?.optante ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:size')}:</Typography>
              <Typography variant="subtitle3">{kyb?.companyDetails?.porte?.descricao ?? '---'}</Typography>
            </div>
            <div className="c-detail-row">
              <Typography variant="subtitle1">{t('common:isReportingEntity')}:</Typography>
              <Typography variant="subtitle3">
                {kyb?.companyDetails?.isReportingEntity ? t('common:yes') : t('common:no')}
              </Typography>
            </div>
          </>
        )}
        <div className="c-detail-row">
          <Typography variant="subtitle1">{t('common:phones')}:</Typography>
          <Typography variant="subtitle3">{finalPhones ?? '---'}</Typography>
        </div>
        <div className="c-detail-row">
          <Typography variant="subtitle1">{t('common:email')}:</Typography>
          <Typography variant="subtitle3">{finalEmails || '---'}</Typography>
        </div>
      </Paper>

      {finalAddress !== null && (
        <div className="c-detail-col">
          <Paper elevation={0} variant="colorPrimary" sx={{ marginBottom: '.75rem' }}>
            <Typography>Google Maps</Typography>
          </Paper>

          <Typography variant="subtitle4">Informações retornadas do Google Maps:</Typography>
          <div className="c-detail-row">
            <MapStaticComponent
              fixedAddress={finalAddress}
              height={300}
              width={450}
              borderRadius={2}
              onCoordinatesFetched={setCoordinates}
            />
            <MapStaticComponent fixedAddress={finalAddress} height={300} width={450} borderRadius={2} showStreetView />
          </div>
          <TimelineDotLine>
            <Paper elevation={0} variant="rootOutlined" sx={{ flex: 1 }}>
              {coordinates && (
                <div className="c-detail-col">
                  <div className="c-detail-row">
                    <Typography variant="subtitle1">Link Google Maps: </Typography>
                    <Link
                      href={`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}&z=16`}
                      target="_blank"
                      variant="contained"
                      color="primary"
                    >
                      <Typography variant="number">{`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}&z=16`}</Typography>
                    </Link>
                  </div>
                  <div className="c-detail-row">
                    <Typography variant="subtitle1">{t('common:address')}: </Typography>
                    <Typography variant="subtitle3">{finalAddress}</Typography>
                  </div>
                  <div className="c-detail-row">
                    <Typography variant="subtitle1">Latitude: </Typography>
                    <Typography variant="subtitle3">{coordinates.lat}</Typography>
                  </div>
                  <div className="c-detail-row">
                    <Typography variant="subtitle1">Longitude: </Typography>
                    <Typography variant="subtitle3">{coordinates.lng}</Typography>
                  </div>
                </div>
              )}
            </Paper>
          </TimelineDotLine>
        </div>
      )}

      {/* SALTO DE PAGINA */}
      <Box className="page-break" />
      {entity === 'BUSINESS' && (
        <>
          <Paper elevation={0} variant="colorPrimary">
            <Typography>{t('common:companyEconomicActivity')}</Typography>
          </Paper>

          <TimelineDotLine color="#7e65d5">
            <Paper variant="rootOutlined" elevation={0} sx={{ flex: 1 }}>
              <div className="c-detail-grid">
                {currentCountry === 'BR' ? (
                  <>
                    <div className="c-detail-row">
                      <Typography variant="subtitle1">
                        {t('common:mainActivity')} {t('common:class')}
                      </Typography>
                      <Typography variant="subtitle3">{kyb?.companyDetails?.cnae?.fiscal ?? '---'}</Typography>
                    </div>
                    <div className="c-detail-row">
                      <Typography variant="subtitle1">
                        {t('common:mainActivity')} {t('common:description')}
                      </Typography>
                      <Typography variant="subtitle3">{kyb?.companyDetails?.cnae?.descricao ?? '---'}</Typography>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="c-detail-row">
                      <Typography variant="subtitle1">
                        {t('common:mainActivity')} {t('common:class')}
                      </Typography>
                      <Typography variant="subtitle3">
                        {kyb?.companyDetails?.companyPrimaryCategory?.code ?? '---'}
                      </Typography>
                    </div>
                    <div className="c-detail-row">
                      <Typography variant="subtitle1">
                        {t('common:mainActivity')} {t('common:description')}
                      </Typography>
                      <Typography variant="subtitle3">
                        {kyb?.companyDetails?.companyPrimaryCategory?.description ?? '---'}
                      </Typography>
                    </div>
                  </>
                )}
              </div>
            </Paper>
          </TimelineDotLine>

          {kyb?.companyDetails?.cnae?.secundarias && (
            <>
              {kyb.companyDetails.cnae.secundarias.map((element, index) => (
                <TimelineDotLine key={index}>
                  <Paper variant="rootOutlined" elevation={0} sx={{ flex: 1 }}>
                    <div className="c-detail-grid" key={index}>
                      <div className="c-detail-row">
                        <Typography variant="subtitle1">{t('common:secondaryActivity')} ID</Typography>
                        <Typography variant="subtitle3">{element.id ?? '---'}</Typography>
                      </div>
                      <div className="c-detail-row">
                        <Typography variant="subtitle1">{t('common:description')}</Typography>
                        <Typography variant="subtitle3">{element.descricao ?? '---'}</Typography>
                      </div>
                    </div>
                  </Paper>
                </TimelineDotLine>
              ))}
            </>
          )}

          {Array.isArray(kyb?.companyDetails?.companySecondaryCategories) && (
            <>
              {kyb.companyDetails.companySecondaryCategories.map((element, index) => (
                <TimelineDotLine key={index}>
                  <Paper variant="rootOutlined" elevation={0} sx={{ flex: 1 }}>
                    <div className="c-detail-grid" key={index}>
                      <div className="c-detail-row">
                        <Typography variant="subtitle1">{t('common:secondaryActivity')} CODE</Typography>
                        <Typography variant="subtitle3">{element.code ?? '---'}</Typography>
                      </div>
                      <div className="c-detail-row">
                        <Typography variant="subtitle1">{t('common:description')}</Typography>
                        <Typography variant="subtitle3">{element.description ?? '---'}</Typography>
                      </div>
                    </div>
                  </Paper>
                </TimelineDotLine>
              ))}
            </>
          )}
        </>
      )}
    </>
  )
}

export default CompanyDetails
